import { UseQueryResult, useQuery } from '@tanstack/react-query'

import Api from 'services/Api'
import { hashTable } from 'services/Utilities'

import { CACHE_TIME, RESSOURCES_ENDPOINTS, STALE_TIME } from 'Api/constants'
import { LeaveType, Status, requestFieldType } from 'Api/types'
import { queryClient } from 'services/QueryClient'

type Options = {
  page?: number
  limit?: number
  filter?: Record<string, unknown>
  sortBy?: string
}

type ResponseType = {
  success: boolean
  result: { [key: string]: requestFieldType }
  status: Status
  vacationType: requestFieldType[]
  leaveReason: requestFieldType[]
  leaveType: LeaveType
  lateReason: requestFieldType[]
  earlyOutReason: requestFieldType[]
  count: number
}

const requestFieldsQuery = async (options?: Options) => {
  const response: ResponseType = await Api.get(RESSOURCES_ENDPOINTS.requestFields, {
    params: { ...options?.filter, limit: options?.limit, page: options?.page, sort: options?.sortBy }
  })
  return requestFieldsTransformer(response)
}

const requestFieldsTransformer = (requestFields: ResponseType) =>
  ({
    requestFieldsById: requestFields.result,
    requestStatus: requestFields.status,
    // vacationTypesById: hashTable(requestFields.vacationType), // vacationTypesById and timeOffTypesById are the same
    attendanceTypesById: hashTable(requestFields.leaveType, 'id'),
    attendanceTypes: requestFields.leaveType,
    timeOffTypes: requestFields.vacationType,
    timeOffTypesById: hashTable(requestFields.vacationType),
    attendanceReasonsById: hashTable([...requestFields.leaveReason, ...requestFields.lateReason, ...requestFields.earlyOutReason]),
    attendanceReasons: [...requestFields.leaveReason, ...requestFields.lateReason, ...requestFields.earlyOutReason],
    count: requestFields.count ?? 0
  } as const)

type RequestFieldsData = ReturnType<typeof requestFieldsTransformer>

const getQueryConfig = (options?: Options) =>
  ({
    queryKey: ['requestFields', { ...options }],
    queryFn: () => requestFieldsQuery(options),
    cacheTime: CACHE_TIME,
    staleTime: STALE_TIME
  } as const)

type Select<T> = (data: RequestFieldsData) => T

const useRequestFields = <T = RequestFieldsData>(select?: Select<T>, options?: Options) => {
  const config = getQueryConfig(options)
  return useQuery({ ...config, select })
}

const useRequestFieldById = (id: string): UseQueryResult<ResponseType | unknown> => {
  return useRequestFields(data => data.requestFieldsById?.[id])
}

const useRequestFieldTypeById = (id: string): UseQueryResult<requestFieldType | unknown> => {
  return useRequestFields(data => {
    const requestTypesById = { ...data.attendanceTypesById, ...data.timeOffTypesById }
    return requestTypesById?.[id]
  })
}

const prefetchRequestFields = async () => queryClient.prefetchQuery({ ...getQueryConfig() })
const invalidateRequestFields = async (options?: Options) => queryClient.invalidateQueries(getQueryConfig(options).queryKey)
const getRequestFieldsQueryCache = (options?: Options) => queryClient.getQueryData<RequestFieldsData>(getQueryConfig(options).queryKey)

export type { RequestFieldsData, ResponseType }
export { useRequestFields, useRequestFieldById, useRequestFieldTypeById, prefetchRequestFields, invalidateRequestFields, getRequestFieldsQueryCache }
